import React, { useState, useEffect, useRef } from 'react';
import { SearchIcon, MapPinIcon, XMarkIcon, SpinnerIcon } from '@/components/Icons';
import { searchPlace, GoogleMapsResult } from '@/services/googleMapsService';

interface PlaceAutocompleteSearchProps {
  onLocationSelect: (lat: number, lng: number, address: string) => void;
  placeholder?: string;
  className?: string;
}

export const PlaceAutocompleteSearch: React.FC<PlaceAutocompleteSearchProps> = ({
  onLocationSelect,
  placeholder = "Ingresa una dirección o lugar...",
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GoogleMapsResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.trim().length >= 3) {
        setLoading(true);
        const data = await searchPlace(query);
        setResults(data);
        setShowResults(true);
        setLoading(false);
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleSelect = (result: GoogleMapsResult) => {
    const { lat, lng } = result.geometry.location;
    onLocationSelect(lat, lng, result.formatted_address);
    setQuery(result.formatted_address);
    setShowResults(false);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? (
            <SpinnerIcon className="h-5 w-5 text-sky-500 animate-spin" />
          ) : (
            <MapPinIcon className="h-5 w-5 text-gray-400 group-focus-within:text-sky-500 transition-colors" />
          )}
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 sm:text-sm shadow-sm transition-all"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 3 && setShowResults(true)}
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="absolute z-[1000] mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <ul className="max-h-60 overflow-y-auto divide-y divide-gray-50">
            {results.map((result) => (
              <li key={result.place_id}>
                <button
                  className="w-full text-left px-4 py-3 hover:bg-sky-50 flex items-start space-x-3 transition-colors active:bg-sky-100"
                  onClick={() => handleSelect(result)}
                >
                  <MapPinIcon className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700 font-medium line-clamp-2">{result.formatted_address}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
